import React, { useEffect, useState } from "react";
import TeamService from "services/TeamService";
import PostFooter from "./PostFooter";
import PostContent from "./PostContent";
import PostHeader from "./PostHeader";
import classNames from "classnames";
import { useHistory } from "react-router-dom";
import { useTimelineContext } from "store/contexts/timelineContext";
import { useUserContext } from "store/contexts/userContext";
import { useTeamContext } from "store/contexts/teamContext";
import { Modal } from "react-responsive-modal";
import Button from "components/CustomButton";
import CourseService from "services/CourseService";
import { useToasts } from "react-toast-notifications";
import { SURVEY_STATUS } from "store/reducers/surveyReducer";

export default ({ post, index, teamId }) => {
  const history = useHistory();
  const { user } = useUserContext();
  const teamContext = useTeamContext();
  const { addToast } = useToasts();
  const color = (post?.data?.color ?? { name: "white" }).name.toLowerCase();
  const color_gradient = post?.data?.color?.color_gradient ?? ["#fff"];
  const textColor = color === "white" ? "#575F6B" : "white";
  const { selectPost, selectedPost } = useTimelineContext();
  const [showCourseDeletedModal, setShowCourseDeletedModal] = useState(false);
  const [showSurveyDeletedModal, setShowSurveyDeletedModal] = useState(false);
  const [showSurveyClosedModal, setShowSurveyClosedModal] = useState(false);

  // Provide fallback for team context when it's null (e.g., in UserDashboard)
  const team = teamContext?.team || null;

  useEffect(() => {
    if (!selectedPost) return;
    selectPost(post);

    //eslint-disable-next-line
  }, [post]);

  const itemContainerStyles = {
    background:
      post.type === "welcomemessage"
        ? "linear-gradient(#00A3A3, #8AFFFF)"
        : `linear-gradient(0deg, ${
            color_gradient[1] ?? color_gradient[0]
          } 0%, ${color_gradient[0]} 80%)`,
    color: post.type === "welcomemessage" ? "white" : textColor,
    fill: textColor,
    borderRadius: "6px",
    padding: "20px",
  };

  useEffect(() => {
    if (post.type_id !== 13) {
      return;
    }

    (async () => {
      try {
        await TeamService.markWelcomeMessageAsRead(post.team_id);
      } catch (e) {
        console.error(e);
      }
    })();

    //eslint-disable-next-line
  }, [post]);

  const checkCourseExists = async (teamId, courseId) => {
    try {
      await CourseService.getCourse(teamId, courseId);
      return true;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return false;
      }
      console.error("Error checking course:", error);
      return false;
    }
  };

  const redirectPost = async (event) => {
    if (post.type === "trivia") {
      history.push(`/teams/${post.team_id}/wellness/focus-boosters/single-trivia/${post.data.trivia_id}`);
      return;
    }
    if (post.type === "todo-list") {
      history.push(`/teams/${post.team_id}/todo-list/${post.data.id}`);
      return;
    }

    if (post.type === "poll") {
      history.push(
        `/teams/${post.team_id}/poll/${post.data.team_poll_id}/detail`
      );
      return;
    }

    if (post.type === "todo-list-item") {
      history.push(
        `/teams/${post.team_id}/todo-list/${post.data.todo_list_id}/todo-list-item/${post.data.id}`
      );
      return;
    }

    if (post.type === "coursecreation") {
      const courseId = post.data.id;
      // Use provided teamId, fallback to post.team.id, or use post.team_id as last resort
      const currentTeamId = teamId || post.team?.id || post.team_id;
      const courseCreationTeamId = post.team_id; // Team ID where the course was created
      const courseExists = await checkCourseExists(currentTeamId, courseId);
      
      if (!courseExists) {
        setShowCourseDeletedModal(true);
        return;
      }
      
      // Check if the course was created in the current team
      const isCreatedInCurrentTeam = courseCreationTeamId === parseInt(currentTeamId);
      
      // Check if the current user is the creator of the course
      const isCreator = post.user && user && post.user.id === user.id;
      
      // Check if the current user is an admin in the team (only if team context is available)
      const isAdmin = team && [2, 3].includes(team.role_id);
      
      // Determine where to redirect:
      // 1. If user is the creator or an admin in the team where the course was created, redirect to manage page
      // 2. Otherwise, start the course and redirect to view page if successful
      if ((isCreator || isAdmin) && isCreatedInCurrentTeam) {
        // Redirect to manage course page - only for creators and admins in the team where the course was created
        history.push(`/teams/${currentTeamId}/coursebuilder/${courseId}/manage`);
      } else {
        // For regular users, start the course first
        try {
          await CourseService.startCourse(courseId, currentTeamId);
          // If successful, redirect to view course page
          history.push(`/teams/${currentTeamId}/courses/${courseId}/view`);
        } catch (error) {
          console.error("Error starting course:", error);
          addToast("Failed to start the course", { appearance: "error" });
        }
      }
      return;
    }

    if (post.type === "survey") {
      const surveyId = post.data.id;
      // Use provided teamId, fallback to post.team.id, or use post.team_id as last resort
      const currentTeamId = teamId || post.team?.id || post.team_id;
      const surveyCreationTeamId = post.team_id; // Team ID where the survey was created
      
      // Check if survey is closed (status === 0) - quick check before redirect
      const surveyStatus = post.status !== undefined ? post.status : SURVEY_STATUS.INACTIVE;
      if (surveyStatus === SURVEY_STATUS.INACTIVE) {
        setShowSurveyClosedModal(true);
        return;
      }
      
      // Check if the survey was created in the current team
      const isCreatedInCurrentTeam = surveyCreationTeamId === parseInt(currentTeamId);
      
      // Check if the current user is the creator of the survey
      const isCreator = post.user && user && post.user.id === user.id;
      
      // Check if the current user is an admin in the team (only if team context is available)
      const isAdmin = team && [2, 3].includes(team.role_id);
      
      // Redirect immediately - loader will be shown on the destination page
      // 1. If user is the creator or an admin in the team where the survey was created, redirect to manage page
      // 2. Otherwise, redirect to survey page to take the survey
      if ((isCreator || isAdmin) && isCreatedInCurrentTeam) {
        // Redirect to manage survey page with surveyId query parameter to open the manage modal
        history.push(`/teams/${currentTeamId}/wellness/survey/manage?surveyId=${surveyId}`);
      } else {
        // For regular users or assigned surveys, redirect to survey page with surveyId to auto-start the survey
        history.push(`/teams/${currentTeamId}/wellness/survey?surveyId=${surveyId}`);
      }
      return;
    }
  };

  const handleCloseCourseDeletedModal = () => {
    setShowCourseDeletedModal(false);
  };

  const handleCloseSurveyDeletedModal = () => {
    setShowSurveyDeletedModal(false);
  };

  const handleCloseSurveyClosedModal = () => {
    setShowSurveyClosedModal(false);
  };

  return (
    <>
      <div
        className={classNames({
          "posts_data mb-5 maxWidthAdjustment": true,
          clickable: ["poll", "todo-list", "todo-list-item", "trivia", "coursecreation", "survey"].includes(post.type)
            ? true
            : false,
        })}
      >
        <div onClick={redirectPost}>
          <div style={itemContainerStyles} className="postContentAdjustment">
            <PostHeader post={post} color={color} />
            <PostContent post={post} />
            <PostFooter post={post} color={textColor} />
          </div>
        </div>
      </div>

      {/* Course Deleted Modal */}
      <Modal
        open={showCourseDeletedModal}
        onClose={handleCloseCourseDeletedModal}
        showCloseIcon={false}
        classNames={{ modalContainer: "modalContainer", modal: "modalPadding" }}
      >
        <div className="modal-body">
          <h2 className="modalHeader">Course Not Available</h2>
          <p className="modalContent">
            This course is no longer available. It may have been deleted or removed.
          </p>
          <div className="modalAction">
            <Button
              buttonClass="small-btn"
              onClick={handleCloseCourseDeletedModal}
            >
              Okay
            </Button>
          </div>
        </div>
      </Modal>

      {/* Survey Deleted Modal */}
      <Modal
        open={showSurveyDeletedModal}
        onClose={handleCloseSurveyDeletedModal}
        showCloseIcon={false}
        classNames={{ modalContainer: "modalContainer", modal: "modalPadding" }}
      >
        <div className="modal-body">
          <h2 className="modalHeader">Survey Not Available</h2>
          <p className="modalContent">
            This survey is no longer available. It may have been deleted or removed.
          </p>
          <div className="modalAction">
            <Button
              buttonClass="small-btn"
              onClick={handleCloseSurveyDeletedModal}
            >
              Okay
            </Button>
          </div>
        </div>
      </Modal>

      {/* Survey Closed Modal */}
      <Modal
        open={showSurveyClosedModal}
        onClose={handleCloseSurveyClosedModal}
        showCloseIcon={false}
        classNames={{ modalContainer: "modalContainer", modal: "modalPadding" }}
      >
        <div className="modal-body">
          <h2 className="modalHeader">Survey Closed</h2>
          <p className="modalContent">
            This survey has been closed and is no longer accepting responses.
          </p>
          <div className="modalAction">
            <Button
              buttonClass="small-btn"
              onClick={handleCloseSurveyClosedModal}
            >
              Okay
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
